import { Injectable, NgZone } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PosthogService {

  private initialized = false;

  constructor(private ngZone: NgZone) {}

  init(): void {
    if (this.initialized || !environment.posthogKey) {
      return;
    }

    // runOutsideAngular: o gravador de sessão do posthog-js (rrweb) registra
    // listeners nativos de document (mousemove, scroll, input...) pra
    // capturar a sessão. Zone.js patcheia addEventListener globalmente, então
    // sem isso cada mousemove do usuário entra na zone do Angular e dispara
    // change detection da página inteira — em produção (única com posthogKey
    // preenchido) isso causava re-render constante de listas/menus abertos
    // com o simples movimento do mouse, inclusive perdendo cliques no meio
    // de um re-render. Stage nunca tinha esse sintoma por rodar com posthogKey
    // vazio (session recording desligado de propósito).
    this.ngZone.runOutsideAngular(() => {
      posthog.init(environment.posthogKey, {
        api_host: environment.posthogHost,
        person_profiles: 'identified_only',
        // O autocapture padrão do PostHog escuta a History API do navegador, mas
        // não pega de forma confiavel as trocas de rota internas do Angular
        // Router (Zone.js patcheia varias APIs assincronas por baixo) — por
        // isso desligamos aqui e capturamos manualmente via capturePageview()
        // nos eventos NavigationEnd do Router.
        capture_pageview: false,
        // Autocapture padrao de window.onerror/unhandledrejection — cobre erros
        // fora da zona do Angular (ex: dentro de um setTimeout sem tratamento).
        // Erros lançados dentro da zona do Angular (templates, componentes) são
        // engolidos pelo ErrorHandler padrão antes de chegarem aqui, por isso
        // também registramos PosthogErrorHandler no app.module.ts.
        capture_exceptions: true,
        // Desligado: bug conhecido do autocapture de Web Vitals do proprio
        // posthog-js lançando TypeError não tratada dentro do SDK (categoria
        // de bug igual ao github.com/PostHog/posthog-js/issues/3579, ainda sem
        // fix oficial). Não usávamos essa métrica mesmo.
        capture_performance: {
          web_vitals: false,
        },
      });
    });

    this.initialized = true;
  }

  capturePageview(path: string): void {
    if (!this.initialized) {
      return;
    }
    posthog.capture('$pageview', {
      $current_url: window.location.origin + path,
    });
  }

  captureException(error: unknown): void {
    if (!this.initialized) {
      return;
    }
    posthog.captureException(error);
  }

  identify(userId: string | number, properties?: Record<string, any>): void {
    if (!this.initialized) {
      return;
    }
    posthog.identify(String(userId), properties);
  }

  reset(): void {
    if (!this.initialized) {
      return;
    }
    posthog.reset();
  }
}
